import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { Camera, Upload, Trash2, ImagePlus, Calendar, ArrowLeftRight } from "lucide-react";

export default function ProgressPhotos() {
  const { token } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [deletePhotoId, setDeletePhotoId] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]); // up to 2 photo ids
  const [filterType, setFilterType] = useState("all");

  // Upload form
  const [photoData, setPhotoData] = useState(null); // base64
  const [photoType, setPhotoType] = useState("progress");
  const [caption, setCaption] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/progress-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load progress photos");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoData(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!photoData) {
      toast.error("Please select a photo");
      return;
    }
    setUploading(true);
    try {
      const res = await axios.post(`${API}/progress-photos`,
        { photo_url: photoData, photo_type: photoType, caption: caption || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotos((prev) => [res.data, ...prev]);
      toast.success("Photo uploaded!");
      setShowUpload(false);
      setPhotoData(null);
      setCaption("");
      setPhotoType("progress");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      if (msg.includes("migration")) {
        toast.error("Database not ready. Run SQL migration in Supabase first.");
      } else {
        toast.error(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/progress-photos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      toast.success("Photo deleted");
      setDeletePhotoId(null);
      setSelectedPhoto(null);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const toggleCompareSelect = (id) => {
    setCompareSelection((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const filteredPhotos = filterType === "all" ? photos : photos.filter((p) => p.photo_type === filterType);

  const typeBadgeColor = (type) => {
    if (type === "before") return "bg-amber-100 text-amber-700";
    if (type === "after") return "bg-emerald-100 text-emerald-700";
    return "bg-blue-100 text-blue-700";
  };

  const compareLeft = compareSelection[0] ? photos.find((p) => p.id === compareSelection[0]) : null;
  const compareRight = compareSelection[1] ? photos.find((p) => p.id === compareSelection[1]) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50/30 via-white to-emerald-50/30">
      <Navbar />
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in-up">
          <div>
            <h1 className="font-serif text-4xl text-primary mb-2">Skin Progress</h1>
            <p className="text-muted-foreground">
              Track your skin journey with before/after photos. {photos.length > 0 && `${photos.length} photo${photos.length === 1 ? "" : "s"} captured.`}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant={compareMode ? "default" : "outline"}
              onClick={() => { setCompareMode(!compareMode); setCompareSelection([]); }}
              className={compareMode ? "btn-primary" : "border-primary/30"}
              disabled={photos.length < 2}
              data-testid="toggle-compare-mode-btn"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              {compareMode ? "Exit Compare" : "Compare"}
            </Button>
            <Button
              onClick={() => setShowUpload(true)}
              className="btn-primary"
              data-testid="upload-photo-btn"
            >
              <ImagePlus className="w-4 h-4 mr-2" />
              Upload Photo
            </Button>
          </div>
        </div>

        {/* Filter */}
        {photos.length > 0 && !compareMode && (
          <div className="flex gap-2 mb-6 animate-fade-in-up">
            {["all", "before", "after", "progress"].map((t) => (
              <button
                key={t}
                onClick={() => setFilterType(t)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-all ${
                  filterType === t
                    ? "bg-primary text-white shadow-sm"
                    : "bg-white/70 text-foreground/70 hover:bg-primary/10"
                }`}
                data-testid={`filter-${t}`}
              >
                {t === "all" ? `All (${photos.length})` : `${t} (${photos.filter((p) => p.photo_type === t).length})`}
              </button>
            ))}
          </div>
        )}

        {/* Compare Mode */}
        {compareMode && (
          <div className="mb-6">
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4 text-sm text-primary">
              Select 2 photos to compare side-by-side ({compareSelection.length}/2 selected)
            </div>
            {compareLeft && compareRight && (
              <div className="grid md:grid-cols-2 gap-4 mb-6 animate-fade-in-up">
                {[compareLeft, compareRight].map((p, idx) => (
                  <div key={p.id} className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-primary/30">
                    <div className="px-4 py-2 bg-primary/5 flex items-center justify-between">
                      <Badge className={typeBadgeColor(p.photo_type)}>{idx === 0 ? "Left" : "Right"}: {p.photo_type}</Badge>
                      <span className="text-xs text-muted-foreground">{new Date(p.taken_at).toLocaleDateString()}</span>
                    </div>
                    <img src={p.photo_url} alt={p.caption || "progress"} className="w-full aspect-square object-cover" />
                    {p.caption && <p className="px-4 py-2 text-sm text-foreground/80">{p.caption}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-16 text-muted-foreground">Loading photos…</div>
        )}

        {/* Empty State */}
        {!loading && photos.length === 0 && (
          <div className="text-center py-16 bg-white/60 rounded-2xl border-2 border-dashed border-primary/20">
            <Camera className="w-16 h-16 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="font-serif text-xl text-primary mb-2">No Photos Yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Document your skincare journey. Take a photo today as your "before" baseline, then snap progress shots every few weeks.
            </p>
            <Button onClick={() => setShowUpload(true)} className="btn-primary" data-testid="empty-upload-btn">
              <ImagePlus className="w-4 h-4 mr-2" />
              Upload First Photo
            </Button>
          </div>
        )}

        {/* Photo Grid */}
        {!loading && filteredPhotos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 animate-fade-in-up stagger-2">
            {filteredPhotos.map((p) => {
              const selected = compareSelection.includes(p.id);
              return (
                <div
                  key={p.id}
                  onClick={() => compareMode ? toggleCompareSelect(p.id) : setSelectedPhoto(p)}
                  className={`relative bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-lg transition-all group ${
                    selected ? "ring-4 ring-primary" : ""
                  }`}
                  data-testid={`photo-card-${p.id}`}
                >
                  <img src={p.photo_url} alt={p.caption || "progress"} className="w-full aspect-square object-cover" />
                  <div className="absolute top-2 left-2">
                    <Badge className={`${typeBadgeColor(p.photo_type)} capitalize text-xs`}>{p.photo_type}</Badge>
                  </div>
                  {selected && (
                    <div className="absolute top-2 right-2 bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold">
                      {compareSelection.indexOf(p.id) + 1}
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
                    <p className="text-xs flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(p.taken_at).toLocaleDateString()}
                    </p>
                    {p.caption && <p className="text-xs truncate mt-0.5 opacity-90">{p.caption}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-lg glass border-white/40" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-primary">Upload Progress Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* File input */}
            <div>
              <Label>Photo</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="photo-file-input"
              />
              {photoData ? (
                <div className="relative mt-2">
                  <img src={photoData} alt="preview" className="w-full max-h-64 object-cover rounded-lg" />
                  <button
                    onClick={() => { setPhotoData(null); fileInputRef.current.value = ""; }}
                    className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 hover:bg-white"
                    data-testid="remove-photo-btn"
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full mt-2 border-2 border-dashed border-primary/30 rounded-lg py-8 flex flex-col items-center gap-2 hover:bg-primary/5 transition-all"
                  data-testid="select-photo-btn"
                >
                  <Upload className="w-8 h-8 text-primary/60" />
                  <span className="text-sm text-foreground/70">Click to select photo (max 5MB)</span>
                </button>
              )}
            </div>

            {/* Type */}
            <div>
              <Label>Type</Label>
              <Select value={photoType} onValueChange={setPhotoType}>
                <SelectTrigger className="mt-2" data-testid="photo-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">Before (baseline)</SelectItem>
                  <SelectItem value="progress">Progress</SelectItem>
                  <SelectItem value="after">After (milestone)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Caption */}
            <div>
              <Label>Caption (optional)</Label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="e.g. Week 4 of new routine"
                className="mt-2"
                rows={2}
                data-testid="photo-caption-input"
              />
            </div>

            <Button
              onClick={handleUpload}
              disabled={!photoData || uploading}
              className="w-full btn-primary"
              data-testid="confirm-upload-btn"
            >
              {uploading ? "Uploading…" : "Upload Photo"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Detail Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="sm:max-w-2xl glass border-white/40" data-testid="photo-detail-dialog">
          {selectedPhoto && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Badge className={`${typeBadgeColor(selectedPhoto.photo_type)} capitalize`}>
                    {selectedPhoto.photo_type}
                  </Badge>
                  <span className="text-sm font-normal text-muted-foreground">
                    {new Date(selectedPhoto.taken_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                  </span>
                </DialogTitle>
              </DialogHeader>
              <img src={selectedPhoto.photo_url} alt={selectedPhoto.caption || "progress"} className="w-full max-h-[70vh] object-contain rounded-lg" />
              {selectedPhoto.caption && (
                <p className="text-sm text-foreground/80 mt-2">{selectedPhoto.caption}</p>
              )}
              <Button
                variant="outline"
                onClick={() => setDeletePhotoId(selectedPhoto.id)}
                className="border-destructive/30 text-destructive hover:bg-destructive/5"
                data-testid="delete-photo-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Photo
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletePhotoId} onOpenChange={() => setDeletePhotoId(null)}>
        <AlertDialogContent data-testid="delete-photo-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-photo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deletePhotoId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-photo-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
